import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { listApiKeys, createApiKey, updateApiKey, revokeApiKey, getAuditLog } from '../api';

const apiKeysKeys = {
  all: ['api-keys'] as const,
  list: () => [...apiKeysKeys.all, 'list'] as const,
  audit: (page: number) => [...apiKeysKeys.all, 'audit', page] as const,
};

export function useApiKeys() {
  return useQuery({
    queryKey: apiKeysKeys.list(),
    queryFn: listApiKeys,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeysKeys.list() });
    },
    onError: () => {
      toast.error('Failed to create API key');
    },
  });
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; scopes?: string[]; rateLimitRpm?: number; expiresAt?: string | null }) =>
      updateApiKey(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeysKeys.list() });
      toast.success('API key updated');
    },
    onError: () => {
      toast.error('Failed to update API key');
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeysKeys.list() });
      toast.success('API key revoked');
    },
    onError: () => {
      toast.error('Failed to revoke API key');
    },
  });
}

export function useAuditLog(page: number) {
  return useQuery({
    queryKey: apiKeysKeys.audit(page),
    queryFn: () => getAuditLog({ pageNumber: page, pageLength: 25 }),
  });
}
